import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import TestimonialSelector from './TestimonialSelector'
import SocialProofStats from './SocialProofStats'

/**
 * TemplateVariableEditor Component
 *
 * Form editor for Remotion video template variables.
 * Groups variables by category and provides appropriate input types.
 *
 * Props:
 * - templateId: Template identifier
 * - variables: Current template variables
 * - onChange: Callback when variables change
 * - disabled: Disable editing
 */

// Variable definitions by template (using i18n keys)
const VARIABLE_SCHEMAS = {
  MinimalistReveal: {
    product: [
      { key: 'product_title', labelKey: 'templateEditor.fields.productTitle', type: 'text', required: true },
      { key: 'sale_price', labelKey: 'templateEditor.fields.salePrice', type: 'text', required: true },
      { key: 'original_price', labelKey: 'templateEditor.fields.originalPrice', type: 'text', placeholderKey: 'templateEditor.placeholders.priceExample' },
    ],
    content: [
      { key: 'headline_text', labelKey: 'templateEditor.fields.headlineText', type: 'text', placeholderKey: 'templateEditor.placeholders.headline' },
    ],
    branding: [
      { key: 'brand_primary_color', labelKey: 'templateEditor.fields.brandColor', type: 'color', defaultValue: '#1a1a2e' },
      { key: 'accent_color', labelKey: 'templateEditor.fields.accentColor', type: 'color', defaultValue: '#6366F1' },
      { key: 'brand_logo', labelKey: 'templateEditor.fields.logoUrl', type: 'url', placeholder: 'https://...' },
    ],
    social: '__SOCIAL_PROOF_STATS__',
  },
  UrgencyFlashSale: {
    product: [
      { key: 'product_title', labelKey: 'templateEditor.fields.productTitle', type: 'text', required: true },
      { key: 'sale_price', labelKey: 'templateEditor.fields.offerPrice', type: 'text', required: true },
      { key: 'original_price', labelKey: 'templateEditor.fields.originalPrice', type: 'text', required: true },
      { key: 'discount_percent', labelKey: 'templateEditor.fields.discountPercent', type: 'number', min: 0, max: 100 },
    ],
    branding: [
      { key: 'brand_primary_color', labelKey: 'templateEditor.fields.brandColor', type: 'color', defaultValue: '#8B0000' },
      { key: 'accent_color', labelKey: 'templateEditor.fields.accentColor', type: 'color', defaultValue: '#FFD700' },
    ],
    urgency: [
      { key: 'discount_code', labelKey: 'templateEditor.fields.discountCode', type: 'text', placeholder: 'FLASH50' },
      // Optional: Only shows countdown if value provided (prevents fake urgency)
      { key: 'countdown_seconds', labelKey: 'templateEditor.fields.countdownSeconds', type: 'number', min: 60, placeholder: '3600' },
      // Optional: Only shows stock warning if value provided (prevents fake scarcity)
      { key: 'stock_count', labelKey: 'templateEditor.fields.stockCount', type: 'number', min: 1, placeholder: '12' },
    ],
  },
  SocialProofShowcase: {
    product: [
      { key: 'product_title', labelKey: 'templateEditor.fields.productTitle', type: 'text', required: true },
      { key: 'sale_price', labelKey: 'templateEditor.fields.price', type: 'text', required: true },
    ],
    branding: [
      { key: 'brand_primary_color', labelKey: 'templateEditor.fields.brandColor', type: 'color', defaultValue: '#1a2e1a' },
      { key: 'accent_color', labelKey: 'templateEditor.fields.accentColor', type: 'color', defaultValue: '#4CAF50' },
    ],
    testimonial: '__TESTIMONIAL_SELECTOR__',
    social: '__SOCIAL_PROOF_STATS__',
  },
  FeatureDemoFlow: {
    product: [
      { key: 'product_title', labelKey: 'templateEditor.fields.productTitle', type: 'text', required: true },
      { key: 'sale_price', labelKey: 'templateEditor.fields.price', type: 'text', required: true },
    ],
    branding: [
      { key: 'brand_primary_color', labelKey: 'templateEditor.fields.brandColor', type: 'color', defaultValue: '#0f0f23' },
      { key: 'accent_color', labelKey: 'templateEditor.fields.accentColor', type: 'color', defaultValue: '#4ECDC4' },
    ],
    problem: [
      { key: 'problem_description', labelKey: 'templateEditor.fields.problemDescription', type: 'textarea', placeholderKey: 'templateEditor.placeholders.problem', rows: 2 },
    ],
    features: [
      { key: 'feature_1', labelKey: 'templateEditor.fields.feature1', type: 'text', required: true, placeholderKey: 'templateEditor.placeholders.feature1' },
      { key: 'feature_2', labelKey: 'templateEditor.fields.feature2', type: 'text', placeholderKey: 'templateEditor.placeholders.feature2' },
      { key: 'feature_3', labelKey: 'templateEditor.fields.feature3', type: 'text', placeholderKey: 'templateEditor.placeholders.feature3' },
    ],
    benefit: [
      { key: 'benefit_description', labelKey: 'templateEditor.fields.benefitDescription', type: 'textarea', placeholderKey: 'templateEditor.placeholders.benefit', rows: 2 },
    ],
  },
}

// Category configuration (icons are language-independent)
const CATEGORY_CONFIG = {
  product: { labelKey: 'templateEditor.categories.product', icon: '📦' },
  content: { labelKey: 'templateEditor.categories.content', icon: '✏️' },
  branding: { labelKey: 'templateEditor.categories.branding', icon: '🎨' },
  social: { labelKey: 'templateEditor.categories.social', icon: '⭐' },
  urgency: { labelKey: 'templateEditor.categories.urgency', icon: '⏰' },
  testimonial: { labelKey: 'templateEditor.categories.testimonial', icon: '💬' },
  problem: { labelKey: 'templateEditor.categories.problem', icon: '❓' },
  features: { labelKey: 'templateEditor.categories.features', icon: '✓' },
  benefit: { labelKey: 'templateEditor.categories.benefit', icon: '🎯' },
}

function TemplateVariableEditor({
  templateId,
  variables = {},
  onChange,
  disabled = false,
  product = null,
}) {
  const { t } = useTranslation('approval')
  const [expandedCategories, setExpandedCategories] = useState(['product'])

  const schema = VARIABLE_SCHEMAS[templateId] || {}

  const toggleCategory = useCallback((category) => {
    setExpandedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category]
    )
  }, [])

  const handleChange = useCallback((key, value) => {
    if (onChange) {
      onChange({
        ...variables,
        [key]: value,
      })
    }
  }, [variables, onChange])

  const [selectedReviewIndex, setSelectedReviewIndex] = useState(0)

  const handleReviewSelect = useCallback((review) => {
    if (onChange) {
      onChange({
        ...variables,
        testimonial_quote: review.text,
        customer_name: review.reviewer_name,
      })
    }
  }, [variables, onChange])

  // Helper to get translated placeholder
  const getPlaceholder = (field) => {
    if (field.placeholderKey) {
      return t(field.placeholderKey, { defaultValue: field.placeholder || '' })
    }
    return field.placeholder || ''
  }

  const renderInput = (field) => {
    const value = variables[field.key] ?? field.defaultValue ?? ''
    const placeholder = getPlaceholder(field)

    const baseInputClass = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
      focus:ring-2 focus:ring-purple-500 focus:border-transparent
      disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed
      transition-colors duration-200`

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={placeholder}
            rows={field.rows || 3}
            disabled={disabled}
            className={`${baseInputClass} resize-none`}
          />
        )

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => {
              const val = e.target.value
              if (val === '') {
                handleChange(field.key, '')
              } else {
                const num = Number(val)
                handleChange(field.key, isNaN(num) ? '' : num)
              }
            }}
            min={field.min}
            max={field.max}
            step={field.step || 1}
            disabled={disabled}
            className={baseInputClass}
          />
        )

      case 'color':
        return (
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={value || '#1a1a2e'}
              onChange={(e) => handleChange(field.key, e.target.value)}
              disabled={disabled}
              className="w-10 h-10 rounded-lg border border-gray-300 cursor-pointer disabled:cursor-not-allowed"
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder="#1a1a2e"
              disabled={disabled}
              className={`${baseInputClass} flex-1 font-mono`}
            />
          </div>
        )

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        )

      default: // text
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(field.key, e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className={baseInputClass}
          />
        )
    }
  }

  if (Object.keys(schema).length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>{t('templateEditor.noSchema', 'Keine bearbeitbaren Variablen für dieses Template')}</p>
      </div>
    )
  }

  // Helper to get translated field label
  const getFieldLabel = (field) => {
    if (field.labelKey) {
      return t(field.labelKey, { defaultValue: field.key })
    }
    return field.label || field.key
  }

  // Helper to get translated category label
  const getCategoryLabel = (category) => {
    const config = CATEGORY_CONFIG[category]
    if (config?.labelKey) {
      return t(config.labelKey, { defaultValue: category })
    }
    return category
  }

  return (
    <div className="template-variable-editor space-y-3">
      {Object.entries(schema).map(([category, fields]) => {
        const categoryConfig = CATEGORY_CONFIG[category] || { icon: '📝' }
        const isExpanded = expandedCategories.includes(category)

        return (
          <div
            key={category}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white"
          >
            {/* Category Header */}
            <button
              type="button"
              onClick={() => toggleCategory(category)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">{categoryConfig.icon}</span>
                <span className="font-medium text-gray-800">{getCategoryLabel(category)}</span>
                {Array.isArray(fields) && (
                  <span className="text-xs text-gray-500">({fields.length})</span>
                )}
              </div>
              {isExpanded ? (
                <ChevronUpIcon className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              )}
            </button>

            {/* Category Fields */}
            {isExpanded && (
              <div className="p-4 space-y-4 border-t border-gray-200">
                {fields === '__TESTIMONIAL_SELECTOR__' ? (
                  <TestimonialSelector
                    reviews={variables.available_reviews || []}
                    selectedIndex={selectedReviewIndex}
                    onSelect={(review, index) => {
                      setSelectedReviewIndex(index)
                      handleReviewSelect(review)
                    }}
                    disabled={disabled}
                  />
                ) : fields === '__SOCIAL_PROOF_STATS__' ? (
                  <SocialProofStats
                    rating={variables.rating ? Number(variables.rating) : product?.aggregate_rating}
                    reviewCount={variables.review_count ? Number(variables.review_count) : product?.review_count}
                    customerCount={variables.customer_count ? Number(variables.customer_count) : product?.customer_count}
                  />
                ) : (
                  fields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {getFieldLabel(field)}
                        {field.required && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </label>
                      {renderInput(field)}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* Hint */}
      <p className="text-xs text-gray-500 text-center pt-2">
        {t('templateEditor.hint', { defaultValue: 'Changes will be applied upon approval' })}
      </p>
    </div>
  )
}

TemplateVariableEditor.propTypes = {
  templateId: PropTypes.string.isRequired,
  variables: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  product: PropTypes.object,
}

export default TemplateVariableEditor
